import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import * as authApi from '@/features/auth/auth-api'
import { useAuth } from '@/features/auth/auth-context'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Field } from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'

import { AuthShell } from '../components/auth-shell'

const schema = z
  .object({
    password: z.string().min(8, 'Минимум 8 символов'),
    password_confirmation: z.string().min(8, 'Минимум 8 символов'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Пароли не совпадают',
    path: ['password_confirmation'],
  })

type ResetForm = z.infer<typeof schema>

function extractErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
  return message || fallback
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated } = useAuth()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const token = searchParams.get('token')?.trim() ?? ''
  const email = searchParams.get('email')?.trim() ?? ''
  const missingParams = !token || !email

  const form = useForm<ResetForm>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', password_confirmation: '' },
  })

  if (isAuthenticated) {
    return <Navigate replace to="/projects" />
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      await authApi.resetPasswordViaEmail({
        email,
        token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      })
      setDone(true)
      setTimeout(() => navigate('/login', { replace: true }), 1500)
    } catch (error) {
      setErrorMessage(
        extractErrorMessage(error, 'Не удалось сменить пароль. Ссылка могла устареть.'),
      )
    }
  })

  return (
    <AuthShell>
      <Card className="w-full">
        <CardHeader>
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
            <KeyRound className="h-5 w-5" />
          </div>
          <CardTitle>Новый пароль</CardTitle>
          <CardDescription>
            {missingParams
              ? 'Ссылка для сброса пароля недействительна.'
              : 'Задайте новый пароль для вашего аккаунта.'}
          </CardDescription>
        </CardHeader>

        <CardBody>
          {missingParams ? (
            <div className="space-y-4">
              <p className="text-sm text-[var(--color-danger-fg)]">
                Откройте страницу по ссылке из письма или запросите восстановление заново.
              </p>
              <Link
                className="font-medium text-[var(--color-accent)] hover:underline"
                to="/forgot-password"
              >
                Запросить восстановление
              </Link>
            </div>
          ) : done ? (
            <p className="text-sm text-[var(--color-text-soft)]">
              Пароль изменён. Перенаправляем на страницу входа…
            </p>
          ) : (
            <form className="space-y-4" onSubmit={onSubmit}>
              <Field error={form.formState.errors.password?.message} label="Новый пароль">
                {(props) => (
                  <Input
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    {...form.register('password')}
                    {...props}
                  />
                )}
              </Field>

              <Field
                error={form.formState.errors.password_confirmation?.message}
                label="Повтор пароля"
              >
                {(props) => (
                  <Input
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    {...form.register('password_confirmation')}
                    {...props}
                  />
                )}
              </Field>

              {errorMessage ? (
                <p className="text-sm text-[var(--color-danger-fg)]">{errorMessage}</p>
              ) : null}

              <Button className="w-full" loading={form.formState.isSubmitting} type="submit">
                Сохранить пароль
              </Button>
            </form>
          )}

          {!missingParams && !done ? (
            <p className="mt-4 text-sm text-[var(--color-text-soft)]">
              <Link className="font-medium text-[var(--color-accent)] hover:underline" to="/login">
                Вернуться ко входу
              </Link>
            </p>
          ) : null}
        </CardBody>
      </Card>
    </AuthShell>
  )
}
